import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import {
  Login,
  PinDrop,
  Handshake,
  TrendingUp,
  Image,
  Mic,
  QuestionAnswer,
  People,
  AccessTime,
  Warning,
  Assessment,
  Download,
} from '@mui/icons-material';
import {
  fetchAllMetrics,
  MetricCard,
  formatPeakActivityTime,
  getTimezoneAbbreviation,
  fetchLoginTrends,
} from '../utils/csDashboardHelperFunctions';
import { downloadUserLastLoginReport } from '../utils/downloadLastLoginReport';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

interface CSMetricsProps {
  organization: string;
  startDate: Date | null;
  endDate: Date | null;
  teams: string;
  individuals: string;
}

export default function CSMetrics({
  organization,
  startDate,
  endDate,
  teams,
  individuals,
}: CSMetricsProps) {
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<boolean>(false);
  const [retryingFailed, setRetryingFailed] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [peakActivityTimezone, setPeakActivityTimezone] =
    useState('America/Denver');
  const [loginTrends, setLoginTrends] = useState<{
    data: number[];
    labels: string[];
  } | null>(null);
  const [loginTrendsLoading, setLoginTrendsLoading] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchMetrics = async (retryFailedOnly: boolean = false) => {
    setError(false);
    setRetryingFailed(retryFailedOnly);

    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);

    try {
      const metricsData = await fetchAllMetrics(
        organization,
        startDate,
        endDate,
        abortController.signal,
        setError,
        retryFailedOnly,
        retryFailedOnly ? metrics?._metadata?.failedMetrics || [] : [],
        retryFailedOnly ? metrics : undefined,
        teams !== 'all' ? teams : undefined,
        individuals !== 'all' ? individuals : undefined
      );

      // Only update state if request wasn't aborted
      if (!abortController.signal.aborted && metricsData) {
        setMetrics(metricsData);
      }

      // Fetch login trends if we have at least one month of data
      if (!abortController.signal.aborted && startDate && endDate) {
        const timeDiff = endDate.getTime() - startDate.getTime();
        const oneMonthMs = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

        if (timeDiff >= oneMonthMs) {
          setLoginTrendsLoading(true);
          try {
            const trendsData = await fetchLoginTrends(
              organization,
              startDate,
              endDate,
              abortController.signal,
              teams !== 'all' ? teams : undefined,
              individuals !== 'all' ? individuals : undefined
            );

            if (!abortController.signal.aborted && trendsData) {
              setLoginTrends(trendsData);
            }
          } catch (error) {
            if ((error as Error)?.message !== 'Request aborted') {
              console.error('Error fetching login trends:', error);
            }
          } finally {
            if (!abortController.signal.aborted) {
              setLoginTrendsLoading(false);
            }
          }
        } else {
          // Clear login trends if date range is too short
          setLoginTrends(null);
        }
      }
    } catch (error) {
      // Don't log abort errors as they're expected
      if ((error as Error)?.message !== 'Request aborted') {
        console.error('Error fetching metrics:', error);
        setError(true);
      }
    } finally {
      // Only stop loading if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setLoading(false);
        setRetryingFailed(false);
      }
    }
  };

  const retryFailedMetrics = () => {
    if (metrics?._metadata?.failedMetrics?.length > 0) {
      fetchMetrics(true);
    }
  };

  useEffect(() => {
    fetchMetrics();

    // Cleanup function to abort on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Clear login trends on unmount
      setLoginTrends(null);
    };
  }, [startDate, endDate, organization, teams, individuals]);

  // Clear login trends when date range becomes too short
  useEffect(() => {
    if (startDate && endDate) {
      const timeDiff = endDate.getTime() - startDate.getTime();
      const oneMonthMs = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

      if (timeDiff < oneMonthMs) {
        setLoginTrends(null);
      }
    }
  }, [startDate, endDate]);

  // Use real metrics when available, fallback to mock data
  const metricsData = useMemo(() => {
    if (!metrics?._metadata?.isInitialLoadComplete) {
      return {
        logins: 0,
        pinsCreated: 0,
        dealsCreated: 0,
        dealStageChanges: 0,
        imagesUploaded: 0,
        recordingsTaken: 0,
        askPaivQuestions: 0,
        loginPercentageVsActiveUsers: 0,
        loginPercentageVsTotalUsers: 0,
        totalAllUsers: 0,
        totalActiveUsers: 0,
        activeUsersWithLoginCount: 0,
        loginPercentageVsSeats: 0,
        totalSeats: 0,
        oldVersionUsers: 0,
        avgTimeInApp: 0,
        peakActivityTime: 0,
      };
    }

    return {
      logins: metrics?.logins || 0,
      pinsCreated: metrics?.pinsCreated || 0,
      dealsCreated: metrics?.dealsCreated || 0,
      dealStageChanges: metrics?.dealStageChanges || 0,
      imagesUploaded: metrics?.imagesUploaded || 0,
      recordingsTaken: metrics?.recordingsTaken || 0,
      askPaivQuestions: metrics?.askPaivQuestions || 0,
      loginPercentageVsActiveUsers: metrics?.loginPercentageVsActiveUsers
        ? Number(
            (Number(metrics.loginPercentageVsActiveUsers) * 100).toFixed(2)
          )
        : 0,
      loginPercentageVsTotalUsers: metrics?.loginPercentageVsTotalUsers
        ? Number((Number(metrics.loginPercentageVsTotalUsers) * 100).toFixed(2))
        : 0,
      totalAllUsers: metrics?.totalAllUsers || 0,
      totalActiveUsers: metrics?.totalActiveUsers || 0,
      activeUsersWithLoginCount: metrics?.activeUsersWithLoginCount || 0,
      loginPercentageVsSeats: metrics?.loginPercentageVsSeats
        ? Number((Number(metrics.loginPercentageVsSeats) * 100).toFixed(2))
        : 0,
      totalSeats: metrics?.totalSeats || 0,
      oldVersionUsers: metrics?.outdatedUsers || 0,
      avgTimeInApp: metrics?.avgTimeInApp || 0,
      peakActivityTime: metrics?.peakActivityTime || 0,
    };
  }, [metrics, organization, startDate, endDate]);

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const loginTrendData = useMemo(() => {
    // Check if we have at least one month of data
    if (!startDate || !endDate) {
      return {
        labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
        datasets: [
          {
            label: 'Daily Logins',
            data: [0, 0, 0, 0],
            borderColor: 'rgb(25, 118, 210)',
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            tension: 0.3,
          },
        ],
      };
    }

    const timeDiff = endDate.getTime() - startDate.getTime();
    const oneMonthMs = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

    if (timeDiff < oneMonthMs) {
      return {
        labels: ['Date range too short'],
        datasets: [
          {
            label: 'Daily Logins',
            data: [0],
            borderColor: 'rgb(25, 118, 210)',
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            tension: 0.3,
          },
        ],
      };
    }

    // Use actual login trends data if available
    if (loginTrends && loginTrends.data && loginTrends.labels) {
      return {
        labels: loginTrends.labels,
        datasets: [
          {
            label: 'Daily Logins',
            data: loginTrends.data,
            borderColor: 'rgb(25, 118, 210)',
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            tension: 0.3,
          },
        ],
      };
    }

    // Fallback while loading or if no data
    return {
      labels: ['Loading...', 'Loading...', 'Loading...', 'Loading...'],
      datasets: [
        {
          label: 'Daily Logins',
          data: [0, 0, 0, 0],
          borderColor: 'rgb(25, 118, 210)',
          backgroundColor: 'rgba(25, 118, 210, 0.1)',
          tension: 0.3,
        },
      ],
    };
  }, [loginTrends, startDate, endDate]);

  const activityData = {
    labels: ['Pins', 'Deals', 'Images', 'Recordings', 'Ask Paiv'],
    datasets: [
      {
        label: 'Activity Count',
        data: [
          metricsData.pinsCreated,
          metricsData.dealsCreated,
          metricsData.imagesUploaded,
          metricsData.recordingsTaken,
          metricsData.askPaivQuestions,
        ],
        backgroundColor: [
          'rgba(25, 118, 210, 0.8)',
          'rgba(46, 125, 50, 0.8)',
          'rgba(245, 124, 0.8)',
          'rgba(156, 39, 176, 0.8)',
          'rgba(244, 67, 54, 0.8)',
        ],
        borderColor: [
          'rgba(25, 118, 210, 1)',
          'rgba(46, 125, 50, 1)',
          'rgba(245, 124, 0, 1)',
          'rgba(156, 39, 176, 1)',
          'rgba(244, 67, 54, 1)',
        ],
        borderWidth: 2,
      },
    ],
  };

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight={500} gutterBottom>
            Error fetching some metrics. Please try again.
          </Typography>
          <Button
            onClick={() => fetchMetrics()}
            variant="outlined"
            size="small"
            sx={{ mt: 1 }}
          >
            Retry All
          </Button>
        </Alert>
      )}

      {/* Partial Data Warning */}
      {metrics?._metadata?.isInitialLoadComplete &&
        metrics?._metadata?.hasPartialData &&
        metrics?._metadata?.failedMetrics?.length > 0 && (
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              '& .MuiAlert-message': {
                width: '100%',
              },
            }}
          >
            <Typography variant="body1" fontWeight={500} gutterBottom>
              Partial data loaded - some metrics failed to fetch
            </Typography>
            <Box mt={1}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>
                  Failed to load ({metrics._metadata.failedMetrics.length}/
                  {metrics._metadata.totalMetrics}):
                </strong>
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {metrics._metadata.failedMetrics.map((metric: string) => (
                  <Box
                    key={metric}
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      bgcolor: (theme) =>
                        theme.custom?.background2 || theme.palette.error.light,
                      color: (theme) =>
                        theme.custom?.text || theme.palette.error.contrastText,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                    }}
                  >
                    {metric}
                  </Box>
                ))}
              </Box>
              <Box mt={2} display="flex" gap={1}>
                <Button
                  onClick={retryFailedMetrics}
                  variant="contained"
                  size="small"
                  disabled={retryingFailed}
                  sx={{
                    bgcolor: (theme) =>
                      theme.custom?.primary || theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: (theme) =>
                        theme.custom?.primaryDark || theme.palette.primary.dark,
                    },
                  }}
                >
                  {retryingFailed
                    ? 'Retrying...'
                    : `Retry Failed (${metrics._metadata.failedMetrics.length})`}
                </Button>
                <Button
                  onClick={() => fetchMetrics()}
                  variant="outlined"
                  size="small"
                  disabled={retryingFailed}
                >
                  Retry All
                </Button>
              </Box>
            </Box>
          </Alert>
        )}

      {/* Download Report Button - Only show for specific org */}
      {organization !== 'all' && (
        <Box mb={3} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={async () => {
              setDownloadingReport(true);
              try {
                await downloadUserLastLoginReport({
                  orgid: organization,
                  format: 'csv',
                });
              } catch (error) {
                console.error('Failed to download report:', error);
                alert('Failed to download report. Please try again.');
              } finally {
                setDownloadingReport(false);
              }
            }}
            // disabled={loading || downloadingReport}
            sx={{
              bgcolor: (theme) =>
                theme.custom?.primary || theme.palette.success.main,
              '&:hover': {
                bgcolor: (theme) =>
                  theme.custom?.primaryDark || theme.palette.success.dark,
              },
            }}
          >
            {downloadingReport
              ? 'Downloading...'
              : 'Download Last Login Report'}
          </Button>
        </Box>
      )}

      {/* Loading Indicator */}
      {loading && (
        <Box mb={3}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" mt={1}>
            {retryingFailed
              ? 'Retrying failed metrics...'
              : 'Loading metrics...'}
          </Typography>
        </Box>
      )}

      {/* Key Metrics Grid */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="User Logins"
            value={metricsData.logins.toLocaleString()}
            icon={<Login />}
            color="primary"
            subtitle={
              <>
                Total unique logins this period
                <br />(
                <Box
                  component="span"
                  sx={(theme) => ({ color: theme.palette.primary.main })}
                >
                  <b>
                    {metricsData.activeUsersWithLoginCount.toLocaleString()}
                  </b>{' '}
                </Box>
                are currently active today)
              </>
            }
            trend={12}
            isFallback={metrics?._metadata?.failedMetrics?.includes(
              'User Logins'
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Pins Created"
            value={metricsData.pinsCreated.toLocaleString()}
            icon={<PinDrop />}
            color="secondary"
            subtitle="New & modified pins"
            trend={8}
            isFallback={metrics?._metadata?.failedMetrics?.includes(
              'Pins Created'
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Deals Created"
            value={metricsData.dealsCreated.toLocaleString()}
            icon={<Handshake />}
            color="success"
            subtitle="New opportunities"
            trend={15}
            isFallback={metrics?._metadata?.failedMetrics?.includes(
              'Deals Created'
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Stage Changes"
            value={metricsData.dealStageChanges.toLocaleString()}
            icon={<TrendingUp />}
            color="warning"
            subtitle="Deal progressions"
            trend={-3}
            isFallback={metrics?._metadata?.failedMetrics?.includes(
              'Deal Stage Changes'
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Images Uploaded"
            value={metricsData.imagesUploaded.toLocaleString()}
            icon={<Image />}
            color="info"
            subtitle="Media content added"
            trend={22}
            isFallback={metrics?._metadata?.failedMetrics?.includes(
              'Images Uploaded'
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Recordings Taken"
            value={metricsData.recordingsTaken.toLocaleString()}
            icon={<Mic />}
            color="error"
            subtitle="Audio recordings"
            trend={5}
            isFallback={metrics?._metadata?.failedMetrics?.includes(
              'Recordings Taken'
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Ask Paiv Questions"
            value={metricsData.askPaivQuestions.toLocaleString()}
            icon={<QuestionAnswer />}
            color="primary"
            subtitle="AI assistant queries"
            trend={28}
            isFallback={metrics?._metadata?.failedMetrics?.includes(
              'Ask Paiv Questions'
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Avg Time in App"
            value={metricsData.avgTimeInApp.toLocaleString()}
            icon={<AccessTime />}
            color="success"
            subtitle="Daily active time (minutes)"
            trend={7}
            isFallback={metrics?._metadata?.failedMetrics?.includes(
              'Average Time in App'
            )}
          />
        </Grid>
      </Grid>

      {/* Engagement Metrics */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Login Rate vs Active User Accounts
              </Typography>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {metricsData.loginPercentageVsActiveUsers}%
                </Typography>
                <People color="action" />
              </Box>
              <LinearProgress
                variant="determinate"
                value={metricsData.loginPercentageVsActiveUsers}
                color="success"
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" color="text.secondary" mt={1}>
                Users accounts with status of &apos;active&apos; (
                <Box
                  component="span"
                  sx={(theme) => ({ color: theme.palette.success.main })}
                >
                  <b>{metricsData.totalActiveUsers.toLocaleString()}</b>
                </Box>
                )
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Login Rate vs Total Users Created
              </Typography>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {metricsData.loginPercentageVsTotalUsers}%
                </Typography>
                <People color="action" />
              </Box>
              <LinearProgress
                variant="determinate"
                value={metricsData.loginPercentageVsTotalUsers}
                color="success"
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" color="text.secondary" mt={1}>
                All users accounts created (
                <Box
                  component="span"
                  sx={(theme) => ({ color: theme.palette.success.main })}
                >
                  <b>{metricsData.totalAllUsers.toLocaleString()}</b>
                </Box>
                )
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            elevation={2}
            sx={{
              opacity: organization === 'all' || loading ? 0.5 : 1,
              pointerEvents:
                organization === 'all' || loading ? 'none' : 'auto',
            }}
          >
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Login Rate vs Seat Count
              </Typography>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Typography variant="h4" color="info.main" fontWeight="bold">
                  {organization === 'all' || loading
                    ? 'N/A'
                    : `${metricsData.loginPercentageVsSeats}%`}
                </Typography>
                <Assessment color="action" />
              </Box>
              <LinearProgress
                variant="determinate"
                value={
                  organization === 'all' || loading
                    ? 0
                    : metricsData.loginPercentageVsSeats
                }
                color="info"
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" color="text.secondary" mt={1}>
                Seat utilization rate (
                <Box
                  component="span"
                  sx={(theme) => ({ color: theme.palette.primary.main })}
                >
                  <b>
                    {organization === 'all' || loading
                      ? 'N/A'
                      : metricsData.totalSeats.toLocaleString()}
                  </b>
                </Box>
                )
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={8}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Login Trends
                {loginTrendsLoading && (
                  <Box
                    component="span"
                    sx={{
                      px: 1,
                      py: 0.25,
                      bgcolor: 'info.light',
                      color: 'info.contrastText',
                      borderRadius: 0.5,
                      fontSize: '0.625rem',
                      fontWeight: 500,
                      ml: 1,
                    }}
                  >
                    LOADING
                  </Box>
                )}
              </Typography>

              {/* Show message if date range is too short */}
              {startDate &&
                endDate &&
                endDate.getTime() - startDate.getTime() <
                  30 * 24 * 60 * 60 * 1000 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Login trends require at least one month of data. Please
                      select a longer date range.
                    </Typography>
                  </Alert>
                )}

              <Box height={300}>
                <Line data={loginTrendData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Version Status
                {metrics?._metadata?.failedMetrics?.includes(
                  'Outdated Users'
                ) && (
                  <Box
                    component="span"
                    sx={{
                      px: 1,
                      py: 0.25,
                      bgcolor: 'warning.light',
                      color: 'warning.contrastText',
                      borderRadius: 0.5,
                      fontSize: '0.625rem',
                      fontWeight: 500,
                      ml: 1,
                    }}
                  >
                    FAILED
                  </Box>
                )}
              </Typography>

              {metrics?._metadata?.failedMetrics?.includes('Outdated Users') ? (
                // Show fallback state when metric failed
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Version status data unavailable
                  </Typography>
                </Alert>
              ) : metricsData.oldVersionUsers > 0 ? (
                // Show warning when users are on old versions
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>
                      {metricsData.oldVersionUsers.toLocaleString()} users
                    </strong>{' '}
                    on old versions
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    All users on current version
                  </Typography>
                </Alert>
              )}

              <Box display="flex" alignItems="center" gap={1}>
                <Warning
                  color={
                    metrics?._metadata?.failedMetrics?.includes(
                      'Outdated Users'
                    )
                      ? 'info'
                      : metricsData.oldVersionUsers > 0
                      ? 'warning'
                      : 'success'
                  }
                />
                <Typography variant="body2" color="text.secondary">
                  {metrics?._metadata?.failedMetrics?.includes('Outdated Users')
                    ? 'Data unavailable'
                    : metricsData.oldVersionUsers > 0
                    ? 'Update recommended'
                    : 'All up to date'}
                </Typography>
              </Box>

              {metrics?._metadata?.failedMetrics?.includes(
                'Outdated Users'
              ) && (
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ fontStyle: 'italic', mt: 1, display: 'block' }}
                >
                  Warning: metric failed to load
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Activity Breakdown
              </Typography>
              <Box height={300}>
                <Bar data={activityData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Metrics */}
      <Card elevation={2}>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6" fontWeight={600}>
              Additional Insights
            </Typography>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Timezone</InputLabel>
              <Select
                value={peakActivityTimezone}
                onChange={(e) => setPeakActivityTimezone(e.target.value)}
                label="Timezone"
                MenuProps={{
                  PaperProps: {
                    sx: (theme) => ({
                      backgroundColor:
                        theme.custom?.background2 ||
                        theme.palette.background.paper,
                      boxShadow: '0px 4px 24px 0px #ABABAB5E !important',
                      border: '1px solid #D0D2D8',
                      borderRadius: '8px',
                    }),
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
              >
                <MenuItem value="America/Denver">MST (Mountain)</MenuItem>
                <MenuItem value="America/New_York">EST (Eastern)</MenuItem>
                <MenuItem value="America/Chicago">CST (Central)</MenuItem>
                <MenuItem value="America/Los_Angeles">PST (Pacific)</MenuItem>
                <MenuItem value="America/Anchorage">AKST (Alaska)</MenuItem>
                <MenuItem value="Pacific/Honolulu">HST (Hawaii)</MenuItem>
                <MenuItem value="Europe/London">GMT (London)</MenuItem>
                <MenuItem value="Europe/Paris">CET (Paris)</MenuItem>
                <MenuItem value="Europe/Berlin">CET (Berlin)</MenuItem>
                <MenuItem value="Asia/Tokyo">JST (Tokyo)</MenuItem>
                <MenuItem value="Asia/Shanghai">CST (Shanghai)</MenuItem>
                <MenuItem value="Asia/Kolkata">IST (India)</MenuItem>
                <MenuItem value="Australia/Sydney">AEST (Sydney)</MenuItem>
                <MenuItem value="UTC">UTC</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Grid item xs={12} sm={6}>
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              p={2}
              bgcolor="grey.50"
              borderRadius={2}
            >
              <AccessTime color="secondary" />
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  flexWrap="nowrap"
                >
                  <Typography
                    variant="body1"
                    fontWeight={500}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Peak Activity Time
                  </Typography>
                </Box>
                <Typography variant="h6" color="secondary">
                  {formatPeakActivityTime(
                    metricsData.peakActivityTime,
                    peakActivityTimezone
                  )}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  All times shown in{' '}
                  {getTimezoneAbbreviation(peakActivityTimezone)}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
}
